import styled from 'styled-components';
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  width: calc(100% + 64px);
  margin-left: -32px;
  margin-right: -32px;
  padding: 48px 32px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 1400px) {
    width: calc(100% + 48px);
    margin-left: -24px;
    margin-right: -24px;
    padding-left: 24px;
    padding-right: 24px;
  }
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 38px;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
  color: var(--text-primary);

  span {
    color: var(--accent-gold, #996515);
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 480px;
  margin: 0 auto;
`;
// ============================================
// STATS GRID
// ============================================
export const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  width: 100%;
  margin: -24px 0 32px 0;
  position: relative;
  z-index: 10;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    gap: 12px;
  }
`;
export const StatCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #996515), var(--accent-gold-light, #B8860B));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(153, 101, 21, 0.25));
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .stat-value {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 28px;
    font-weight: 600;
    color: var(--accent-gold, #996515);
    margin-bottom: 4px;
  }

  .stat-label {
    color: var(--text-secondary);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  @media (max-width: 768px) {
    padding: 16px;

    .stat-value {
      font-size: 22px;
    }
  }
`;
// ============================================
// TABS
// ============================================
export const TabsContainer = styled.div `
  width: calc(100% + 64px);
  margin-left: -32px;
  margin-right: -32px;
  padding: 0 32px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);

  @media (max-width: 1400px) {
    width: calc(100% + 48px);
    margin-left: -24px;
    margin-right: -24px;
    padding: 0 24px;
  }

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;
export const TabsList = styled.div `
  display: flex;
  gap: 0;

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    width: 100%;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }
`;
export const Tab = styled.button `
  padding: 14px 20px;
  color: ${props => props.$active ? 'var(--accent-gold, #996515)' : 'var(--text-secondary)'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? 'var(--accent-gold, #996515)' : 'transparent'};
  background: transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
    background: var(--bg-secondary);
  }

  svg {
    width: 14px;
    height: 14px;
  }

  /* Tablet - 4 column grid */
  @media (max-width: 900px) {
    padding: 12px 8px;
    font-size: 12px;
    border-bottom: 2px solid ${props => props.$active ? 'var(--accent-gold, #996515)' : 'transparent'};

    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Mobile - 2x2 grid */
  @media (max-width: 600px) {
    padding: 12px 10px;
    border-bottom: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);

    &:nth-child(2n) {
      border-right: none;
    }

    &:nth-child(n+3) {
      border-bottom: 2px solid ${props => props.$active ? 'var(--accent-gold, #996515)' : 'transparent'};
    }
  }
`;
export const TabBadge = styled.span `
  background: var(--accent-gold, #996515);
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  width: 100%;
  padding: 28px 0;

  @media (max-width: 1024px) {
    padding: 24px 0;
  }

  @media (max-width: 768px) {
    padding: 20px 0;
  }

  @media (max-width: 480px) {
    padding: 16px 0;
  }
`;
// ============================================
// FILTER SECTION
// ============================================
export const FilterSection = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-bottom: 28px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: 20px;
    margin-bottom: 24px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 20px;
  }
`;
export const FilterHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
export const FilterTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary);
  margin: 0;

  svg {
    color: var(--accent-gold, #996515);
    width: 18px;
    height: 18px;
  }
`;
export const SearchBar = styled.div `
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
export const SearchInput = styled.input `
  flex: 1;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #996515);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(153, 101, 21, 0.08));
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
export const AISearchButton = styled.button `
  background: linear-gradient(135deg, var(--accent-gold, #996515), var(--accent-gold-light, #B8860B));
  color: #fff;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 13px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(153, 101, 21, 0.3);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
export const FilterGroup = styled.div `
  margin-bottom: 16px;
`;
export const FilterLabel = styled.div `
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  font-weight: 600;
`;
export const FilterRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;
export const PillGrid = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
export const Pill = styled.button `
  padding: 8px 14px;
  background: ${props => props.$active ? 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.$active ? 'var(--accent-gold, #996515)' : 'var(--text-secondary)'};
  font-weight: 500;

  &:hover {
    border-color: var(--accent-gold-border, rgba(153, 101, 21, 0.25));
    color: var(--text-primary);
    background: var(--bg-tertiary);
  }
`;
export const RangeGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
// 4-column range grid for Size & Price inputs - with tablet breakpoint
export const RangeGridFour = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
export const RangeInputGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 11px;
    color: var(--text-secondary);
    font-weight: 500;
  }
`;
export const RangeInput = styled.input `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 10px 12px;
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #996515);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(153, 101, 21, 0.08));
  }
`;
export const FilterActions = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    gap: 16px;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;

    > * {
      width: 100%;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
`;
export const ClearButton = styled.button `
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 10px 18px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    border-color: var(--text-primary);
    color: var(--text-primary);
    background: var(--bg-secondary);
  }
`;
export const ExportButton = styled.button `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 10px 18px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    border-color: var(--accent-gold, #996515);
    background: var(--accent-gold-bg, rgba(153, 101, 21, 0.08));
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
// ============================================
// RESULTS SECTION
// ============================================
export const ResultsHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;
export const ResultsCount = styled.div `
  font-size: 13px;
  color: var(--text-secondary);

  strong {
    color: var(--accent-gold, #996515);
    font-weight: 600;
  }
`;
export const SortDropdown = styled.select `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 8px 12px;
  color: var(--text-primary);
  font-size: 12px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #996515);
  }
`;
export const GridContainer = styled.div `
  display: flex;
  gap: 20px;

  @media (max-width: 1024px) {
    gap: 16px;
  }

  @media (max-width: 900px) {
    /* Pagination sidebar is hidden, so grid takes full width */
    flex-direction: column;
  }
`;
export const DiamondsGridWrapper = styled.div `
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  max-height: calc(100vh - 250px);
  padding-right: 8px;

  @media (max-width: 1024px) {
    max-height: calc(100vh - 200px);
  }

  @media (max-width: 900px) {
    width: 100%;
    max-height: none;
    overflow-y: visible;
    padding-right: 0;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bg-tertiary);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
  }
`;
export const DiamondsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;
// ============================================
// DIAMOND CARD (Updated Styling)
// ============================================
export const DiamondCardStyled = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: var(--accent-gold-border, rgba(153, 101, 21, 0.25));
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #996515), var(--accent-gold-light, #B8860B));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::after {
    opacity: 1;
  }
`;
export const CardImage = styled.div `
  position: relative;
  padding: 20px;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;

  img {
    max-width: 100px;
    max-height: 100px;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
    transition: transform 0.3s;
  }

  ${DiamondCardStyled}:hover & img {
    transform: scale(1.05) rotate(2deg);
  }
`;
export const WatchlistButton = styled.button `
  position: absolute;
  top: 10px;
  right: 10px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.$active ? '#ef4444' : 'var(--text-tertiary)'};
  z-index: 5;

  &:hover {
    color: #ef4444;
    border-color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: ${props => props.$active ? 'currentColor' : 'none'};
  }
`;
export const LabBadge = styled.span `
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${props => {
    switch (props.$lab?.toUpperCase()) {
        case 'GIA': return '#0056b3';
        case 'AGS': return '#1a1a1a';
        case 'IGI': return '#c41e3a';
        case 'HRD': return '#003366';
        default: return 'var(--accent-gold, #996515)';
    }
}};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  letter-spacing: 0.5px;
  z-index: 5;
`;
export const CardContent = styled.div `
  padding: 16px;
`;
export const CardTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
`;
export const CardSpecs = styled.div `
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;
export const SpecTag = styled.span `
  background: var(--bg-secondary);
  padding: 4px 10px;
  font-size: 11px;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  font-weight: 500;
`;
export const CardDetails = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 12px;
`;
export const CardPrice = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 22px;
  font-weight: 600;
  color: var(--accent-gold, #996515);
  margin-bottom: 12px;
`;
export const CardActions = styled.div `
  display: flex;
  gap: 8px;
`;
export const CardButton = styled.button `
  flex: 1;
  padding: 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  border: 1px solid ${props => props.$primary || props.$active ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  background: ${props => {
    if (props.$primary)
        return 'var(--accent-gold, #996515)';
    if (props.$active)
        return 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))';
    return 'var(--bg-secondary)';
}};
  color: ${props => {
    if (props.$primary)
        return '#fff';
    if (props.$active)
        return 'var(--accent-gold, #996515)';
    return 'var(--text-primary)';
}};

  &:hover {
    border-color: var(--accent-gold, #996515);
    background: ${props => props.$primary ? 'var(--accent-gold-light, #B8860B)' : 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const CardButtonFull = styled.button `
  width: 100%;
  margin-top: 8px;
  padding: 10px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-secondary);
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    border-color: var(--accent-gold, #996515);
    color: var(--accent-gold, #996515);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
// ============================================
// PAGINATION SIDEBAR
// ============================================
export const PaginationSidebar = styled.div `
  width: 52px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  position: sticky;
  top: 90px;
  align-self: flex-start;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 46px;
    gap: 4px;
    padding: 8px 0;
  }

  @media (max-width: 900px) {
    display: none;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 2px;
  }
`;
export const PageButton = styled.button `
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$active ? 'var(--accent-gold, #996515)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  color: ${props => props.$active ? '#fff' : 'var(--text-secondary)'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: ${props => props.$active ? '600' : '500'};

  &:hover {
    border-color: var(--accent-gold, #996515);
    color: ${props => props.$active ? '#fff' : 'var(--accent-gold, #996515)'};
  }
`;
// ============================================
// MOBILE PAGINATION
// ============================================
export const MobileTopBar = styled.div `
  display: none;

  @media (max-width: 900px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    margin-bottom: 12px;
  }
`;
export const MobileBottomBar = styled.div `
  display: none;

  @media (max-width: 900px) {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    margin-top: 16px;
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    margin-top: 12px;
    gap: 6px;
  }
`;
export const MobilePaginationButton = styled.button `
  padding: 8px 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  font-size: 13px;
  transition: all 0.2s;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: var(--bg-tertiary);
    border-color: var(--accent-gold, #996515);
  }
`;
export const MobilePageInfo = styled.span `
  color: var(--text-primary);
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  padding: 0 8px;
`;
// ============================================
// FLOATING COMPARE BUTTON
// ============================================
export const FloatingCompareButton = styled.button `
  position: fixed;
  bottom: 24px;
  right: 180px;
  background: var(--accent-gold, #996515);
  color: #fff;
  padding: 14px 24px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 32px rgba(153, 101, 21, 0.35);
  transition: all 0.3s;
  z-index: 9997;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(153, 101, 21, 0.45);
    background: var(--accent-gold-light, #B8860B);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
export const CompareCount = styled.span `
  background: #fff;
  color: var(--accent-gold, #996515);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
`;
// ============================================
// ACCESS BANNERS
// ============================================
export const AccessBanner = styled.div `
  background: ${props => {
    switch (props.$variant) {
        case 'warning': return 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))';
        case 'success': return 'rgba(34, 197, 94, 0.08)';
        default: return 'var(--bg-secondary)';
    }
}};
  border: 1px solid ${props => {
    switch (props.$variant) {
        case 'warning': return 'var(--accent-gold, #996515)';
        case 'success': return '#22c55e';
        default: return 'var(--border-color)';
    }
}};
  padding: 24px;
  margin-bottom: 24px;
  text-align: center;

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 18px;
    margin-bottom: 12px;
    color: ${props => {
    switch (props.$variant) {
        case 'warning': return 'var(--accent-gold, #996515)';
        case 'success': return '#22c55e';
        default: return 'var(--text-primary)';
    }
}};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  p {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 16px;
    line-height: 1.6;
  }
`;
export const AccessDenied = styled.div `
  text-align: center;
  padding: 60px 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  margin: 48px auto;
  max-width: 600px;

  h2 {
    font-family: 'Playfair Display', 'Adamina', serif;
    color: var(--text-primary);
    margin-bottom: 24px;
    font-size: 24px;
  }

  p {
    color: var(--text-secondary);
    margin-bottom: 24px;
    line-height: 1.6;
  }
`;
// ============================================
// CONTENT SECTIONS (for other tabs)
// ============================================
export const ContentSection = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 32px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;
export const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
`;
export const InfoGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
export const InfoCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 24px;

  h4 {
    font-family: 'Playfair Display', 'Adamina', serif;
    color: var(--text-primary);
    margin-bottom: 16px;
    font-size: 16px;
  }

  p {
    color: var(--text-secondary);
    line-height: 1.8;
    font-size: 14px;
  }
`;
export const EmptyState = styled.div `
  padding: 48px;
  text-align: center;
  color: var(--text-tertiary);
  border: 1px dashed var(--border-color);
  font-size: 14px;
`;
// ============================================
// SKELETON LOADING
// ============================================
import { keyframes } from 'styled-components';
const shimmer = keyframes `
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;
const SkeletonBase = styled.div `
  background: linear-gradient(
    90deg,
    var(--bg-tertiary, #2a2a2a) 25%,
    var(--bg-secondary, #1f1f1f) 50%,
    var(--bg-tertiary, #2a2a2a) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;
export const SkeletonCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  overflow: hidden;
`;
export const SkeletonImage = styled(SkeletonBase) `
  width: 100%;
  aspect-ratio: 1;
`;
export const SkeletonContent = styled.div `
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
export const SkeletonTitle = styled(SkeletonBase) `
  height: 20px;
  width: 70%;
`;
export const SkeletonSpecs = styled.div `
  display: flex;
  gap: 8px;
`;
export const SkeletonSpec = styled(SkeletonBase) `
  height: 24px;
  width: 45px;
  border-radius: 12px;
`;
export const SkeletonText = styled(SkeletonBase) `
  height: 14px;
  width: 85%;
`;
export const SkeletonPrice = styled(SkeletonBase) `
  height: 28px;
  width: 40%;
  margin-top: 4px;
`;
export const SkeletonActions = styled.div `
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;
export const SkeletonButton = styled(SkeletonBase) `
  height: 36px;
  flex: 1;
`;
export const LoadingContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;
export const LoadingHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  color: var(--text-secondary);
  font-size: 14px;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
